import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { reportsApi } from "../../api/endpoints.js";
import type { Reporte } from "../../api/types.js";
import { ScreenHeader, Content } from "../../components/Layout.js";
import { Card } from "../../components/Card.js";
import { Button } from "../../components/Button.js";
import { SharedAudio, PlayButton } from "../../components/AudioPlayer.js";
import { ErrorBanner } from "../../components/Form.js";
import { Loader } from "../../components/Loader.js";
import { useQuery } from "../../hooks/useQuery.js";
import { useSignedAudioPlayer } from "../../hooks/useSignedAudioPlayer.js";
import { useT } from "../../i18n/index.js";
import styles from "./TherapistReportPage.module.css";

export function TherapistReportPage() {
  const t = useT();
  const { sesionId } = useParams<{ sesionId: string }>();
  const id = sesionId ?? "";
  const { data, error, loading, reload } = useQuery<Reporte>(
    () => reportsApi.generar(id, "terapeuta"),
    [id],
  );

  // Player unificado (un solo <audio> por foco); consulta puntual → sin auto-avance.
  const audioItems = useMemo(
    () =>
      (data?.porPalabra ?? []).map((item, i) => ({
        id: `${item.palabraObjetivo}-${i}`,
        audioUrl: item.audioUrl ?? null,
      })),
    [data],
  );
  const player = useSignedAudioPlayer({
    items: audioItems,
    onExpired: () => reload(),
  });

  return (
    <>
      <ScreenHeader title="Reporte del terapeuta" back="/terapeuta" />
      <Content>
        {loading ? (
          <Loader label={t("common.loading")} />
        ) : error ? (
          <>
            <ErrorBanner message={error} />
            <Button variant="ghost" onClick={reload}>
              {t("common.retry")}
            </Button>
          </>
        ) : data ? (
          <>
            <Card>
              <div className={styles.stats}>
                <div className={styles.stat}>
                  <b>{data.totalPalabras}</b>
                  <span>Palabras</span>
                </div>
                <div className={styles.stat}>
                  <b>{data.palabrasCorrectas}</b>
                  <span>Correctas</span>
                </div>
                <div className={styles.stat}>
                  <b>{data.palabrasEnRevision}</b>
                  <span>En revisión</span>
                </div>
              </div>
              <p className={styles.trace} style={{ marginTop: 10 }}>
                Trazabilidad — engine {data.engineVersion ?? "—"} · inventario{" "}
                {data.inventoryVersion ?? "—"}
              </p>
            </Card>

            <Card>
              <h2>Detalle por palabra</h2>
              <div className={styles.tableWrap} style={{ marginTop: 10 }}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Palabra</th>
                      <th>Fonemas objetivo</th>
                      <th>Fonemas producidos</th>
                      <th>Correcta</th>
                      <th>Códigos</th>
                      <th>Revisión</th>
                      <th>Audio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.porPalabra.map((item, i) => (
                      <tr key={`${item.palabraObjetivo}-${i}`}>
                        <td style={{ fontWeight: 700 }}>{item.palabraObjetivo}</td>
                        <td>{item.fonemasObjetivo.join(" ")}</td>
                        <td>{item.fonemasProducidos.join(" ")}</td>
                        <td className={item.correcta ? styles.ok : styles.bad}>
                          {item.correcta ? "✓" : "✗"}
                        </td>
                        <td style={{ whiteSpace: "normal" }}>
                          {item.detecciones.length > 0
                            ? item.detecciones.map((d, j) => (
                                <span key={`${d.codigo}-${j}`} className={styles.chip} title={d.detalle}>
                                  {d.codigo}
                                </span>
                              ))
                            : "—"}
                        </td>
                        <td>{item.revisionHumana ? "👁 revisión" : ""}</td>
                        <td>
                          {item.audioUrl ? (
                            <PlayButton player={player} id={`${item.palabraObjetivo}-${i}`} />
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Único <audio> compartido; lo dirigen los ▶ de cada fila. */}
              <SharedAudio player={player} />
            </Card>

            <Card>
              <h2>Procesos detectados</h2>
              {data.agregadosPorProceso.length > 0 ? (
                <ul style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                  {data.agregadosPorProceso.map((a) => (
                    <li key={a.codigo} className={styles.chip}>
                      {a.codigo} × {a.conteo}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Sin procesos agregados.</p>
              )}
            </Card>

            <Card>
              <h2>Narrativa</h2>
              <p style={{ marginTop: 6 }}>{data.narrativa.resumen}</p>
              <p style={{ marginTop: 10, fontWeight: 700 }}>Recomendaciones</p>
              <p>{data.narrativa.recomendaciones}</p>
            </Card>
          </>
        ) : null}
      </Content>
    </>
  );
}
